/**
 * Boards Event Bus
 *
 * A lightweight, typed pub/sub for boards-internal communication.
 * Replaces scattered window.dispatchEvent / window.addEventListener patterns
 * with a testable, type-safe alternative.
 *
 * Usage:
 *   Producer:  publish("task-updated", { taskId: "abc" })
 *   Consumer:  subscribe("task-updated", (detail) => { ... })
 *         or:  subscribeByKey("task-updated", "taskId", "abc", () => { ... })
 */

type BoardsEventMap = {
   "task-updated": { taskId: string };
   "project-updated": { projectId: string };
   "column-tasks-changed": { projectId: string };
};

type BoardsEventType = keyof BoardsEventMap;
type Listener<T extends BoardsEventType> = (detail: BoardsEventMap[T]) => void;

const listeners = new Map<BoardsEventType, Set<Listener<any>>>();

const keyedListeners = new Map<string, Set<Listener<any>>>();

function compositeKey(event: string, keyField: string, keyValue: string): string {
   return `${event}:${keyField}:${keyValue}`;
}

export function subscribe<T extends BoardsEventType>(event: T, listener: Listener<T>): () => void {
   if (!listeners.has(event)) {
      listeners.set(event, new Set());
   }
   listeners.get(event)!.add(listener);

   return () => {
      listeners.get(event)?.delete(listener);
   };
}

/**
 * Subscribe to an event only when a specific key in the detail matches a value.
 * This avoids every subscriber receiving every event and filtering internally.
 *
 * Example: subscribeByKey("task-updated", "taskId", "abc-123", () => forceUpdate())
 * Only fires when publish("task-updated", { taskId: "abc-123" }) is called.
 */
export function subscribeByKey<T extends BoardsEventType>(
   event: T,
   keyField: string & keyof BoardsEventMap[T],
   keyValue: string,
   listener: Listener<T>,
): () => void {
   const key = compositeKey(event, keyField, keyValue);
   if (!keyedListeners.has(key)) {
      keyedListeners.set(key, new Set());
   }
   keyedListeners.get(key)!.add(listener);

   return () => {
      keyedListeners.get(key)?.delete(listener);
      if (keyedListeners.get(key)?.size === 0) {
         keyedListeners.delete(key);
      }
   };
}

export function publish<T extends BoardsEventType>(event: T, detail: BoardsEventMap[T]): void {
   listeners.get(event)?.forEach((listener) => listener(detail));

   for (const [keyField, value] of Object.entries(detail)) {
      const key = compositeKey(event, keyField, value as string);
      keyedListeners.get(key)?.forEach((listener) => listener(detail));
   }
}

/** Reset all subscriptions. Use in test teardown only. */
export function resetBus(): void {
   listeners.clear();
   keyedListeners.clear();
}
